import React from 'react'
import styled from 'styled-components'
import { Pagination } from 'antd'

/**
 * 手機版「列表轉卡片」共用元件
 *
 * 用法：
 *   <MobileRecordList>
 *     {items.map((it) => (
 *       <MobileRecordCard
 *         key={it.id}
 *         title="客戶名 / 員工名"
 *         subtitle="#1　2026-06-19"
 *         extra={<Tag>類型</Tag>}        // 右上角（標籤或重點數字）
 *         rows={[
 *           { label: '電話', value: '0912...' },
 *           { label: '金額', value: 'NT$ 990', valueStyle: { color: 'red' } },
 *           { label: '備註', value: '...', hidden: !it.note }
 *         ]}
 *         actions={<Button>詳情</Button>}  // 底部按鈕列
 *         dimmed={it.voided}              // 淡化（作廢/取消）
 *       />
 *     ))}
 *   </MobileRecordList>
 *   <MobileCardPagination current={...} pageSize={...} total={...} onChange={...} />
 *
 * 設計目標：各列表頁手機版樣式統一、之後新頁面直接套用、不必各自重寫 styled。
 */

export const MobileRecordList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  ${(p) => (p.$padded ? 'padding: 12px;' : '')}
`

const Card = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  padding: 14px;
  background: ${(p) => (p.$dimmed ? '#fafafa' : '#fff')};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  opacity: ${(p) => (p.$dimmed ? 0.7 : 1)};
`

const Top = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
`

const TitleText = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  word-break: break-word;
`

const Sub = styled.div`
  font-size: 12px;
  color: #999;
  margin-top: 2px;
`

const Extra = styled.div`
  flex-shrink: 0;
  text-align: right;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: flex-end;
`

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  font-size: 13px;
  padding: 3px 0;
`

const Label = styled.span`
  color: #999;
  white-space: nowrap;
  flex-shrink: 0;
`

const Value = styled.span`
  color: #333;
  text-align: right;
  word-break: break-word;
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #f3f4f6;
`

const PagWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 0 4px;
`

export const MobileRecordCard = ({
  title,
  subtitle,
  extra,
  rows = [],
  actions,
  dimmed = false,
  children
}) => (
  <Card $dimmed={dimmed}>
    <Top>
      <div style={{ minWidth: 0 }}>
        {title != null && <TitleText>{title}</TitleText>}
        {subtitle != null && <Sub>{subtitle}</Sub>}
      </div>
      {extra != null && <Extra>{extra}</Extra>}
    </Top>

    {rows
      .filter((r) => r && !r.hidden)
      .map((r, i) => (
        <Row key={i}>
          <Label>{r.label}</Label>
          <Value style={r.valueStyle}>{r.value}</Value>
        </Row>
      ))}

    {children}

    {actions != null && <Actions>{actions}</Actions>}
  </Card>
)

export const MobileCardPagination = ({
  current,
  pageSize,
  total,
  onChange,
  showTotal
}) => (
  <PagWrap>
    <Pagination
      current={current}
      pageSize={pageSize}
      total={total}
      size='small'
      showSizeChanger={false}
      showTotal={showTotal || ((t) => `共 ${t} 筆`)}
      onChange={onChange}
    />
  </PagWrap>
)

export default MobileRecordCard
